import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { StatusPill } from '@/components/ui/StatusPill';
import { isChargerSelectable } from '@/components/points/ChargerStatusBadge';
import { formatDistance } from '@/lib/geo';
import { countAvailableConnectors, peakKw } from '@/lib/stationStats';
import type { StationDetailOut } from '@/api/stations';

interface Props {
  station: StationDetailOut;
  /** Distância até o usuário em km (client-side); null = sem posição. */
  distanceKm?: number | null;
  /** Primeira da lista ordenada por distância → selo "Mais próxima". */
  nearest?: boolean;
}

interface ChargerSummary {
  /** Ex.: "DC 60 kW", "AC 7 kW" */
  label: string;
  /** Quantidade de cabos físicos com esse perfil. */
  count: number;
}

/**
 * Agrega os chargers da station em buckets por (tipo elétrico × potência).
 * "DC 60 kW × 2" significa 2 conectores DC de 60 kW (pode ser 1 charger
 * dual-plug ou 2 chargers single-plug).
 */
function summarizeChargers(station: StationDetailOut): ChargerSummary[] {
  const buckets = new Map<string, number>();
  for (const charger of station.chargers) {
    for (const conn of charger.connectors) {
      const isAc = conn.type === 'Type2';
      const kw = Math.round(Number(conn.max_power_kw));
      const label = `${isAc ? 'AC' : 'DC'} ${kw} kW`;
      buckets.set(label, (buckets.get(label) ?? 0) + 1);
    }
  }
  return Array.from(buckets.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

export function StationCard({ station, distanceKm = null, nearest = false }: Props) {
  const navigate = useNavigate();
  const { available, total } = countAvailableConnectors(station);
  const summary = summarizeChargers(station);
  const peak = Math.round(peakKw(station));
  const multiCharger = station.chargers.length > 1;

  // Atalho: quando a station tem só 1 charger, abrir direto a tela de
  // recarga. O ConfigureCharge ja tem selector de conector quando tem >1
  // cabo, entao o user nao perde nada — só pula um passo redundante.
  const onClick = () => {
    if (station.chargers.length === 1 && station.chargers[0].connectors.length > 0) {
      const charger = station.chargers[0];
      const target =
        charger.connectors.find((c) => isChargerSelectable(c.state)) ??
        charger.connectors[0];
      navigate(`/configure/${charger.id}/${target.id}`);
    } else {
      navigate(`/stations/${station.id}`);
    }
  };

  return (
    <button type="button" onClick={onClick} className="w-full text-left">
      <Card className="flex gap-4 p-4 transition hover:shadow-mid">
        {station.photo_url ? (
          <img
            src={station.photo_url}
            alt={station.name}
            width={192}
            height={192}
            loading="lazy"
            decoding="async"
            className="h-24 w-24 flex-shrink-0 rounded-2xl object-cover [@media(max-height:740px)]:h-20 [@media(max-height:740px)]:w-20"
          />
        ) : (
          <div className="flex h-24 w-24 flex-shrink-0 items-center justify-center rounded-2xl bg-bow-royal/5 [@media(max-height:740px)]:h-20 [@media(max-height:740px)]:w-20">
            <BoltGlyph />
          </div>
        )}
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-1.5">
            {nearest && <StatusPill tone="blue">📍 Mais próxima</StatusPill>}
            {available > 0 ? (
              <StatusPill tone="green">
                {available === 1 ? '1 livre' : `${available} livres`}
              </StatusPill>
            ) : (
              <StatusPill tone="muted">Em uso</StatusPill>
            )}
            {peak > 0 && (
              <span className="text-[11px] font-semibold text-bow-royal/55">
                até {peak} kW
              </span>
            )}
            {distanceKm !== null && (
              <span className="text-[11px] font-semibold text-bow-royal/55">
                · {formatDistance(distanceKm)}
              </span>
            )}
          </div>
          <span
            className="leading-tight"
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 17,
              letterSpacing: '-0.02em'
            }}
          >
            {station.name}
          </span>
          <span className="text-xs text-bow-royal/60 [@media(max-height:740px)]:hidden">
            {station.address}
          </span>
          <div className="mt-0.5 flex flex-wrap items-center gap-1">
            {multiCharger ? (
              // Vários carregadores: "N carregadores" em vez dos pills por tipo —
              // 2 carregadores idênticos ficavam indistinguíveis de 1 com 2
              // conectores (mapfirst-multi.jsx · MultiListRow).
              <span className="rounded-full bg-bow-royal/5 px-2 py-0.5 text-[11px] font-semibold text-bow-royal/70">
                {station.chargers.length} carregadores
              </span>
            ) : (
              <>
                {summary.map((s) => (
                  <span
                    key={s.label}
                    className="rounded-full bg-bow-royal/5 px-2 py-0.5 text-[11px] font-semibold text-bow-royal/70"
                  >
                    {s.label}
                    {s.count > 1 && (
                      <span className="ml-1 text-bow-royal/45">× {s.count}</span>
                    )}
                  </span>
                ))}
                {total > 0 && summary.length === 0 && (
                  <span className="text-[11px] text-bow-royal/55">
                    {total} {total === 1 ? 'conector' : 'conectores'}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </Card>
    </button>
  );
}

function BoltGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      fill="rgba(0,27,116,0.35)"
      aria-hidden
    >
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}
